import { create } from "zustand";
import {
  WordSelectionLibraries,
  FillInBlankLibraries,
  WordSelectionItem,
  FillInBlankItem,
  GameMode,
} from "../types/game";
import { v4 as uuid } from 'uuid';

interface GameStore {
  gameId: string;

  selectedLibrary: string | null;
  currentLibrary: string | null;
  currentMode: GameMode | null;

  // 词库相关
  wordSelectionLibraries: WordSelectionLibraries;
  fillInBlankLibraries: FillInBlankLibraries;

  // 游戏状态
  isGameActive: boolean;

  // 计时器相关状态
  timerStartTime: number | null;
  timerElapsedTime: number;
  isTimerRunning: boolean;

  // 设置初始数据的方法
  setSelectionLibraries: (data: WordSelectionLibraries) => void;
  setFillInBlankLibraries: (data: FillInBlankLibraries) => void;

  // 获取词库数据的方法
  getAllLibraryNames: () => string[];
  getCurrentLibraryItems: () => WordSelectionItem[] | FillInBlankItem[] | null;

  // 游戏控制方法
  startGame: (mode: GameMode, library?: string) => void;
  pauseGame: () => void;
  resumeGame: () => void;
  endGame: () => void;

  // 计时器控制方法
  startTimer: () => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  resetTimer: () => void;
  updateTimerElapsed: () => void;
  getFormattedTime: () => string;
}

const useGameStore = create<GameStore>((set, get) => ({
  gameId: '',

  // 基础游戏状态
  selectedLibrary: null,
  currentMode: null,
  currentLibrary: null,

  isGameActive: false,

  // 词库数据
  wordSelectionLibraries: {},
  fillInBlankLibraries: {},

  // 计时器状态
  timerStartTime: null,
  timerElapsedTime: 0,
  isTimerRunning: false,

  // 更新词库数据的方法
  setSelectionLibraries: (data) => set({ wordSelectionLibraries: data }),
  setFillInBlankLibraries: (data) => set({ fillInBlankLibraries: data }),

  // 获取词库名称
  getAllLibraryNames: () => {
    const { wordSelectionLibraries, fillInBlankLibraries } = get();

    return [
      ...Object.keys(wordSelectionLibraries),
      ...Object.keys(fillInBlankLibraries),
    ];
  },

  // 获取当前选中词库的内容
  getCurrentLibraryItems: () => {
    const { currentLibrary, wordSelectionLibraries, fillInBlankLibraries } =
      get();

    console.log(
      "library found: selected",
      currentLibrary,
      "slection:",
      wordSelectionLibraries,
      "fillin:",
      fillInBlankLibraries
    );

    if (!currentLibrary) return null;

    // 先检查选择题库
    if (currentLibrary in wordSelectionLibraries) {
      return wordSelectionLibraries[currentLibrary];
    }

    // 再检查填空题库
    if (currentLibrary in fillInBlankLibraries) {
      return fillInBlankLibraries[currentLibrary];
    }

    return null;
  },

  // 游戏控制方法
  startGame: (mode, library) => {
    const {selectedLibrary} = get()
    if (library) {
      set({
        selectedLibrary
      })
    }
    // 设置游戏状态
    set({
      currentMode: mode,
      currentLibrary: library || selectedLibrary,
      isGameActive: true,
      gameId: uuid()
    });
    if (!library && !selectedLibrary) {
      throw Error("No library specified.")
    }

    // 启动计时器
    get().startTimer();
  },

  pauseGame: () => {
    set({ isGameActive: false });
    get().pauseTimer();
  },

  resumeGame: () => {
    set({ isGameActive: true });
    get().resumeTimer();
  },

  endGame: () => {
    set({
      isGameActive: false,
      currentMode: null,
    });
    get().resetTimer();
  },

  // 计时器控制方法
  startTimer: () =>
    set({
      timerStartTime: Date.now(),
      timerElapsedTime: 0,
      isTimerRunning: true,
    }),

  pauseTimer: () =>
    set({
      isTimerRunning: false,
    }),

  resumeTimer: () =>
    set((state) => ({
      timerStartTime: Date.now() - state.timerElapsedTime,
      isTimerRunning: true,
    })),

  resetTimer: () =>
    set({
      timerStartTime: null,
      timerElapsedTime: 0,
      isTimerRunning: false,
    }),

  // 更新计时器经过的时间
  updateTimerElapsed: () =>
    set((state) => {
      if (state.isTimerRunning && state.timerStartTime) {
        return { timerElapsedTime: Date.now() - state.timerStartTime };
      }
      return {};
    }),

  // 获取格式化的时间字符串 (MM:SS 或 HH:MM:SS)
  getFormattedTime: () => {
    const { timerElapsedTime } = get();
    const totalSeconds = Math.floor(timerElapsedTime / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return hours > 0
      ? `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0"
        )}:${String(seconds).padStart(2, "0")}`
      : `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
          2,
          "0"
        )}`;
  },
}));

export default useGameStore;
